// PaymentBreakdownPage.tsx
import React, { Fragment, Suspense, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { ManagementReportMenu } from "@/components/Holizontal/ManagementReportMenu";

import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { cn } from "@/lib/utils";
import { apiFetch } from "@/lib/api";
import { helper } from "@/lib/helper";

import {
  Banknote,
  Percent,
  Receipt,
  AlertTriangle,
  Hash,
  CalendarIcon,
  ChevronDown,
  ChevronUp,
  Search,
  CreditCard,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";

/** ✅ Lazy-load heavy UI (recharts + export libs) */
const Charts = React.lazy(() => import("./components/_PaymentBreakdownCharts"));
const ExportButtons = React.lazy(() => import("./components/_PaymentBreakdownExportButtons"));

type BranchOpt = { id: number; name: string };
type ProductOpt = { id: number; name: string };
type BdoOpt = { id: number; name: string };

type ApiRow = {
  id: number;
  date: string;
  customer: string;
  phone: string;
  loanNo: string;
  branch: string;
  channel: string;
  amount: number | string;
  principal: number | string;
  interest: number | string;
  fees: number | string;
  penalty: number | string;
  balanceAfter: number | string;
  bdo: string;
  product: string;
  status: string;
  txnRef: string;
};

type ApiTotals = {
  total: number;
  principal: number;
  interest: number;
  fees: number;
  penalties: number;
  count: number;
};

type ApiChannel = { name: string; value: number };

type DataResponse = {
  data: ApiRow[];
  meta: { per_page: number; next_cursor: string | null; prev_cursor: string | null; has_more: boolean };
  totals: ApiTotals;
  channels: ApiChannel[];
};

type MetaResponse = {
  branches: BranchOpt[];
  // ✅ optional (recommended): return these once to avoid blocking filters when branch="All"
  products?: ProductOpt[];
  bdos?: BdoOpt[];
};

type BranchMetaResponse = {
  products: ProductOpt[];
  bdos: BdoOpt[];
};

const PER_PAGE = 10;
const API_PATH = "/management/payment-breakdown"; // ✅ DATA only
const META_PATH = "/management/payment-breakdown/meta"; // ✅ branches (+ optionally products/bdos)
const BRANCH_META_PATH = "/management/payment-breakdown/meta-by-branch"; // ✅ products+bdos per-branch

function parseNum(v: any): number {
  if (v == null) return 0;
  if (typeof v === "number") return v;
  const n = Number(String(v).replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function useDebounced<T>(value: T, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const granularities = ["Daily", "Weekly", "Monthly"] as const;

const statusColor: Record<string, string> = {
  Paid: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  Partial: "bg-amber-500/10 text-amber-700 border-amber-500/20",
  Reversed: "bg-red-500/10 text-red-600 border-red-500/20",
  Failed: "bg-destructive/10 text-destructive border-destructive/20",
};

function LoaderLine() {
  return (
    <div className="h-1 w-full bg-muted overflow-hidden rounded">
      <motion.div
        className="h-full bg-primary"
        initial={{ x: "-100%" }}
        animate={{ x: "100%" }}
        transition={{ repeat: Infinity, duration: 1.0, ease: "linear" }}
      />
    </div>
  );
}

export default function PaymentBreakdownPage() {
  const [dateFrom, setDateFrom] = useState<Date | undefined>(new Date(2026, 0, 1));
  const [dateTo, setDateTo] = useState<Date | undefined>(new Date(2026, 1, 28));

  // ✅ default: All (so page can load data immediately)
  const [branchId, setBranchId] = useState<string>("All");
  const [productId, setProductId] = useState<string>("All");
  const [bdoId, setBdoId] = useState<string>("All");

  const [granularity, setGranularity] = useState<(typeof granularities)[number]>("Monthly");

  const [search, setSearch] = useState("");
  const qDebounced = useDebounced(search, 350);

  // data
  const [rows, setRows] = useState<ApiRow[]>([]);
  const [totals, setTotals] = useState<ApiTotals>({
    total: 0,
    principal: 0,
    interest: 0,
    fees: 0,
    penalties: 0,
    count: 0,
  });
  const [channelTotals, setChannelTotals] = useState<ApiChannel[]>([]);

  // meta
  const [branches, setBranches] = useState<BranchOpt[]>([]);
  const [products, setProducts] = useState<ProductOpt[]>([]);
  const [bdos, setBdos] = useState<BdoOpt[]>([]);
  const [metaLoading, setMetaLoading] = useState(false);
  const [branchMetaLoading, setBranchMetaLoading] = useState(false);

  // cursor
  const [cursor, setCursor] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [prevCursor, setPrevCursor] = useState<string | null>(null);

  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  // ui
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** ✅ 1) Load meta once (fast): branches (+ optional products/bdos if your backend returns them) */
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setMetaLoading(true);
        const json = await apiFetch<MetaResponse>(META_PATH);
        if (!alive) return;

        setBranches(json.branches ?? []);
        // ✅ if you return these globally, filters work even when branch = All
        if (json.products) setProducts(json.products);
        if (json.bdos) setBdos(json.bdos);
      } catch {
        if (!alive) return;
        setBranches([]);
      } finally {
        if (alive) setMetaLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  /** ✅ 2) When branch changes (and not All) -> load products+bdos for that branch (optional refinement) */
  useEffect(() => {
    let alive = true;

    // reset dependent filters when branch changes
    setProductId("All");
    setBdoId("All");

    if (!branchId || branchId === "All") {
      // keep any global products/bdos from META_PATH (if present)
      return () => {
        alive = false;
      };
    }

    (async () => {
      try {
        setBranchMetaLoading(true);
        const json = await apiFetch<BranchMetaResponse>(`${BRANCH_META_PATH}?branch_id=${branchId}`);
        if (!alive) return;
        setProducts(json.products ?? []);
        setBdos(json.bdos ?? []);
      } catch {
        if (!alive) return;
        // keep previous/global lists if branch meta fails
      } finally {
        if (alive) setBranchMetaLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [branchId]);

  /** ✅ Reset cursor whenever filters change */
  useEffect(() => {
    setCursor(null);
  }, [dateFrom, dateTo, branchId, productId, bdoId, qDebounced]);

  /** ✅ Allow loading even when branch = All */
  const canLoad = Boolean(dateFrom) && Boolean(dateTo);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("per_page", String(PER_PAGE));

    if (dateFrom) params.set("from", format(dateFrom, "yyyy-MM-dd"));
    if (dateTo) params.set("to", format(dateTo, "yyyy-MM-dd"));

    // ✅ only send branch_id when not All
    if (branchId && branchId !== "All") params.set("branch_id", branchId);

    if (productId !== "All") params.set("product_id", productId);
    if (bdoId !== "All") params.set("bdo_id", bdoId);

    if (qDebounced.trim()) params.set("q", qDebounced.trim());
    if (cursor) params.set("cursor", cursor);

    return params.toString();
  }, [dateFrom, dateTo, branchId, productId, bdoId, qDebounced, cursor]);

  async function load() {
    if (!canLoad) return;

    setLoading(true);
    setError(null);

    try {
      const json = await apiFetch<DataResponse>(`${API_PATH}?${queryString}`);
      setRows(json.data ?? []);
      setTotals(
        json.totals ?? { total: 0, principal: 0, interest: 0, fees: 0, penalties: 0, count: 0 }
      );
      setChannelTotals(json.channels ?? []);
      setNextCursor(json.meta?.next_cursor ?? null);
      setPrevCursor(json.meta?.prev_cursor ?? null);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load report");
      setRows([]);
      setNextCursor(null);
      setPrevCursor(null);
      setTotals({ total: 0, principal: 0, interest: 0, fees: 0, penalties: 0, count: 0 });
      setChannelTotals([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString, canLoad]);

  const resetFilters = () => {
    setBranchId("All");
    setProductId("All");
    setBdoId("All");
    setSearch("");
    setExpandedRow(null);
    setCursor(null);
  };

  const DatePick = ({
    label,
    value,
    onChange,
  }: {
    label: string;
    value?: Date;
    onChange: (d?: Date) => void;
  }) => (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "justify-start text-left font-normal min-w-[130px]",
            !value && "text-muted-foreground"
          )}
        >
          <CalendarIcon className="h-4 w-4 mr-1" />
          {value ? format(value, "dd/MM/yyyy") : label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={onChange}
          initialFocus
          className="p-3 pointer-events-auto"
        />
      </PopoverContent>
    </Popover>
  );

  const branchOptions = useMemo(() => branches, [branches]);
  const productOptions = useMemo(() => [{ id: 0, name: "All Products" }, ...products], [products]);
  const bdoOptions = useMemo(() => [{ id: 0, name: "All BDOs" }, ...bdos], [bdos]);

  const dateFromLabel = dateFrom ? format(dateFrom, "dd/MM/yyyy") : "-";
  const dateToLabel = dateTo ? format(dateTo, "dd/MM/yyyy") : "-";

  return (
    <div className="space-y-6">
      {loading && <LoaderLine />}

      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <motion.h1
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-2xl font-bold text-foreground flex items-center gap-2"
        >
          <CreditCard className="h-6 w-6 text-primary" /> Payment Breakdown
        </motion.h1>

        <div className="hidden sm:flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={resetFilters} disabled={loading}>
            Reset
          </Button>
        </div>
      </div>

      <ManagementReportMenu />

      {/* Filters */}
      <Card className="shadow-card">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-2 items-end">
            <DatePick label="From" value={dateFrom} onChange={setDateFrom} />
            <DatePick label="To" value={dateTo} onChange={setDateTo} />

            {/* ✅ Branch (All by default) */}
            <Select value={branchId} onValueChange={setBranchId}>
              <SelectTrigger className="w-56" disabled={metaLoading}>
                <SelectValue
                  placeholder={metaLoading ? "Loading branches..." : "All Branches"}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Branches</SelectItem>
                {branchOptions.map((b) => (
                  <SelectItem key={b.id} value={String(b.id)}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* BDO (works for All if META_PATH returns global bdos; otherwise it's still okay) */}
            <Select value={bdoId} onValueChange={setBdoId} disabled={branchMetaLoading}>
              <SelectTrigger className={cn("w-44", branchMetaLoading && "opacity-60")}>
                <SelectValue
                  placeholder={branchMetaLoading ? "Loading BDOs..." : "All BDOs"}
                />
              </SelectTrigger>
              <SelectContent>
                {bdoOptions.map((b) => (
                  <SelectItem key={b.id} value={b.id === 0 ? "All" : String(b.id)}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Product (works for All if META_PATH returns global products) */}
            <Select value={productId} onValueChange={setProductId} disabled={branchMetaLoading}>
              <SelectTrigger className={cn("w-56", branchMetaLoading && "opacity-60")}>
                <SelectValue
                  placeholder={branchMetaLoading ? "Loading products..." : "All Products"}
                />
              </SelectTrigger>
              <SelectContent>
                {productOptions.map((p) => (
                  <SelectItem key={p.id} value={p.id === 0 ? "All" : String(p.id)}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Name / phone / loan#"
                className="pl-8 w-56"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* Export (lazy) */}
            <Suspense fallback={null}>
              <ExportButtons
                rows={rows}
                totals={{ total: totals.total, count: totals.count }}
                dateFromLabel={dateFromLabel}
                dateToLabel={dateToLabel}
                disabled={!canLoad || loading}
              />
            </Suspense>

            <Button
              variant="outline"
              size="sm"
              className="sm:hidden"
              onClick={resetFilters}
              disabled={loading}
            >
              Reset
            </Button>
          </div>

          {/* Load/Error */}
          <div className="mt-3 flex items-center justify-between gap-3">
            <div className="text-xs text-muted-foreground">
              {loading ? "Loading..." : `Transactions (filtered): ${totals.count}`}
            </div>
            <div className="text-xs text-muted-foreground">
              Showing: <span className="font-medium text-foreground">{rows.length}</span>
              {totals.count ? (
                <>
                  {" "}
                  of <span className="font-medium text-foreground">{totals.count}</span>
                </>
              ) : null}
            </div>
          </div>

          {error && (
            <div className="mt-3 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          { icon: Banknote, label: "Total Collected", value: helper.formatMoneyKES(totals.total), color: "text-emerald-600" },
          { icon: Banknote, label: "Principal", value: helper.formatMoneyKES(totals.principal), color: "text-sky-600" },
          { icon: Percent, label: "Interest", value: helper.formatMoneyKES(totals.interest), color: "text-violet-600" },
          { icon: Receipt, label: "Fees", value: helper.formatMoneyKES(totals.fees), color: "text-amber-600" },
          { icon: AlertTriangle, label: "Penalties", value: helper.formatMoneyKES(totals.penalties), color: "text-red-600" },
          { icon: Hash, label: "Transactions", value: String(totals.count), color: "text-primary" },
        ].map((c) => (
          <Card key={c.label} className="shadow-card h-full">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <c.icon className={cn("h-4 w-4", c.color)} />
                <span className="text-xs text-muted-foreground">{c.label}</span>
              </div>
              <p className="text-lg font-bold">{c.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts (lazy) */}
      <Suspense
        fallback={
          <Card className="shadow-card">
            <CardContent className="p-6 text-sm text-muted-foreground">Loading charts...</CardContent>
          </Card>
        }
      >
        <Charts
          rows={rows}
          totals={totals}
          channels={channelTotals}
          granularity={granularity}
          setGranularity={setGranularity}
        />
      </Suspense>

      {/* Table */}
      <Card className="shadow-card">
        <div className="px-4 py-3 flex items-center justify-between border-b border-border">
          <div className="font-semibold text-sm">Transactions (Current Page: {rows.length})</div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => prevCursor && setCursor(prevCursor)}
              disabled={!prevCursor || loading}
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => nextCursor && setCursor(nextCursor)}
              disabled={!nextCursor || loading}
            >
              Next <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>

        <div className="relative">
          {/* Overlay loader */}
          {loading && (
            <div className="absolute inset-0 z-10 bg-background/60 backdrop-blur-[1px] flex items-center justify-center">
              <div className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm">
                <motion.span
                  className="h-4 w-4 rounded-full border-2 border-muted-foreground border-t-transparent"
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 0.8, ease: "linear" }}
                />
                Loading transactions...
              </div>
            </div>
          )}

          {/* Desktop */}
          <div className="hidden lg:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Loan#</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Principal</TableHead>
                  <TableHead className="text-right">Interest</TableHead>
                  <TableHead className="text-right">Fees</TableHead>
                  <TableHead className="text-right">Penalty</TableHead>
                  <TableHead className="text-right">Bal After</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {!loading && rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                      No transactions found.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r) => {
                    const isOpen = expandedRow === r.id;
                    return (
                      <Fragment key={r.id}>
                        <TableRow
                          className="cursor-pointer hover:bg-muted/40"
                          onClick={() => setExpandedRow(isOpen ? null : r.id)}
                        >
                          <TableCell className="w-10">
                            {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </TableCell>

                          <TableCell className="whitespace-nowrap">{r.date}</TableCell>
                          <TableCell className="font-medium">
                            <div className="min-w-0">
                              <div className="truncate">{r.customer}</div>
                              <div className="text-xs text-muted-foreground">{r.phone}</div>
                            </div>
                          </TableCell>

                          <TableCell className="whitespace-nowrap">{r.loanNo}</TableCell>
                          <TableCell className="whitespace-nowrap">{r.branch}</TableCell>

                          <TableCell className="text-right font-medium">{helper.formatMoneyKES(parseNum(r.amount))}</TableCell>
                          <TableCell className="text-right">{helper.formatMoneyKES(parseNum(r.principal))}</TableCell>
                          <TableCell className="text-right">{helper.formatMoneyKES(parseNum(r.interest))}</TableCell>
                          <TableCell className="text-right">{helper.formatMoneyKES(parseNum(r.fees))}</TableCell>
                          <TableCell className="text-right">{helper.formatMoneyKES(parseNum(r.penalty))}</TableCell>
                          <TableCell className="text-right">{helper.formatMoneyKES(parseNum(r.balanceAfter))}</TableCell>
                        </TableRow>

                        {isOpen && (
                          <TableRow>
                            <TableCell colSpan={11} className="bg-muted/30 px-8 py-3">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <span className="text-muted-foreground">Txn Ref:</span>{" "}
                                  <span className="font-mono">{r.txnRef}</span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Product:</span> {r.product}
                                </div>
                                <div>
                                  <span className="text-muted-foreground">BDO:</span> {r.bdo}
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Status:</span>{" "}
                                  <Badge className={cn("border", statusColor[r.status] || "bg-muted text-foreground border-border")}>
                                    {r.status}
                                  </Badge>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Mobile */}
          <div className="lg:hidden space-y-3 p-4">
            {!loading && rows.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No transactions found</p>
            ) : (
              rows.map((r) => {
                const isOpen = expandedRow === r.id;
                return (
                  <Card key={r.id} className="shadow-card">
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-semibold text-sm truncate">{r.customer}</div>
                          <div className="text-xs text-muted-foreground">{r.phone}</div>
                          <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-2 gap-y-1">
                            <span>{r.date}</span>
                            <span>•</span>
                            <span className="truncate">{r.branch}</span>
                            <span>•</span>
                            <span className="truncate">{r.channel}</span>
                          </div>
                        </div>

                        <Badge
                          className={cn(
                            "border shrink-0",
                            statusColor[r.status] || "bg-muted text-foreground border-border"
                          )}
                        >
                          {r.status}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="rounded-lg border border-border p-2">
                          <div className="text-muted-foreground">Amount</div>
                          <div className="font-bold">{helper.formatMoneyKES(parseNum(r.amount))}</div>
                        </div>
                        <div className="rounded-lg border border-border p-2">
                          <div className="text-muted-foreground">Loan</div>
                          <div className="font-semibold truncate">{r.loanNo}</div>
                        </div>
                        <div className="rounded-lg border border-border p-2">
                          <div className="text-muted-foreground">Principal</div>
                          <div className="font-semibold">{helper.formatMoneyKES(parseNum(r.principal))}</div>
                        </div>
                        <div className="rounded-lg border border-border p-2">
                          <div className="text-muted-foreground">Interest</div>
                          <div className="font-semibold">{helper.formatMoneyKES(parseNum(r.interest))}</div>
                        </div>
                      </div>

                      <button
                        onClick={() => setExpandedRow(isOpen ? null : r.id)}
                        className="text-xs text-primary underline"
                      >
                        {isOpen ? "Hide details" : "View details"}
                      </button>

                      {isOpen && (
                        <div className="text-xs pt-2 border-t border-border space-y-1">
                          <p>
                            BDO: <span className="font-medium">{r.bdo}</span>
                          </p>
                          <p>
                            Product: <span className="font-medium">{r.product}</span>
                          </p>
                          <p>
                            Txn Ref: <span className="font-mono">{r.txnRef}</span>
                          </p>
                          <p>
                            Fees: {helper.formatMoneyKES(parseNum(r.fees))} • Penalty:{" "}
                            {helper.formatMoneyKES(parseNum(r.penalty))}
                          </p>
                          <p>Balance After: {helper.formatMoneyKES(parseNum(r.balanceAfter))}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}

            <div className="flex items-center justify-between gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => prevCursor && setCursor(prevCursor)}
                disabled={!prevCursor || loading}
                className="inline-flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" /> Prev
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => nextCursor && setCursor(nextCursor)}
                disabled={!nextCursor || loading}
                className="inline-flex items-center gap-2"
              >
                Next <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}